import { Audio } from "react-loader-spinner"
import { css } from "@emotion/css"
import { useEffect, useMemo, useState } from "react"
import AttachMoneyIcon from "@material-ui/icons/AttachMoney"
import { UserCell } from "./UserCell"
import { useMutation, useQuery } from "@apollo/client"
import { UPDATE_USER, User, USERS_QUERY, USER_QUERY } from "AdminApp"
import { sort } from "ramda"
import { useLazyQuery } from "@apollo/client"
import { MetricsToolbar } from "./MetricsToolbar"
import { useUpdateEffect } from "react-use"
import { humanizeFileSize } from "./humanizeFileSize"
import PaidChart from "PaidChart"

export type SortingOptions =
  | "createdAt"
  | "numInvites"
  | "paid"
  | "mobile"
  | "desktop"
  | "lastSeen"
  | "downgraded"

const UserList: React.FC = () => {
  const [userTree, setUserTree] = useState<(string | null)[]>([null])
  const [sortedBy, setSortedBy] = useState<SortingOptions>("createdAt")
  const [includeAnon, setIncludeAnon] = useState(false)
  const [selectedUser, setSelectedUser] = useState<null | User>(null)
  const [chartMode, setChartMode] = useState(false)
  const onToggleChartMode = () => setChartMode((prev) => !prev)
  const addRow = (user: User, iteration: number) => {
    const tempList = userTree
    const trimmedList = tempList.slice(0, iteration + 1)
    setUserTree([...trimmedList, user.id])
    setSelectedUser(user)
  }

  const [getUser] = useLazyQuery(USER_QUERY)
  const onClickInvitedBy = async () => {
    const id = selectedUser?.invitedBy?.id
    if (!id) return
    // fetch the user
    const res = await getUser({ variables: { id } })
    if (res.data?._user) {
      const user = res.data?._user
      setSelectedUser(user)
      addRow(user, 0)
    }
  }

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [updateUser] = useMutation(UPDATE_USER)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  useEffect(() => setConfirmDelete(false), [selectedUser])
  const onClickDelete = () => {
    if (confirmDelete && selectedUser) {
      const userId = selectedUser.id
      updateUser({ variables: { id: userId, deleted: true } })
        .then(() => {
          setRefetchTrigger((prev) => prev + 1)
          setConfirmDelete(false)
          setSelectedUser(null)
        })
        .catch((err) => {
          console.log("Failed to delete", err)
        })
    } else {
      setConfirmDelete(true)
    }
  }

  const [confirmBan, setConfirmBan] = useState(false)
  const onClickBan = () => {
    if (confirmBan && selectedUser) {
      const userId = selectedUser.id
      const shouldUnban = selectedUser.banned === true
      updateUser({
        variables: { id: userId, banned: shouldUnban ? false : true },
      })
        .then(() => {
          setRefetchTrigger((prev) => prev + 1)
          setConfirmBan(false)
        })
        .catch((err) => {
          console.log("Failed to delete", err)
        })
    } else {
      setConfirmBan(true)
    }
  }

  useEffect(() => {
    setConfirmBan(false)
    setConfirmDelete(false)
  }, [selectedUser?.id])
  return (
    <div
      style={{
        paddingLeft: 60,
        paddingTop: 60,
        overflow: "scroll",
      }}
    >
      {chartMode ? <PaidChart /> : null}
      <div
        style={{
          width: 400,
          borderRadius: 20,
          padding: 20,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            visibility: selectedUser ? "visible" : "hidden",
          }}
        >
          <p style={{ fontSize: 16 }}>
            {selectedUser?.username ?? "select a user"}
          </p>
          <p
            onClick={() => {
              if (selectedUser) {
                window.open(
                  `https://mixpanel.com/project/2587795/view/3126979/app/profile#distinct_id=${selectedUser.id}`
                )
              }
            }}
            style={{
              fontSize: 16,
              marginLeft: 120,
              opacity: selectedUser ? 0.1 : 0,
              cursor: selectedUser ? "pointer" : "default",
            }}
          >
            {"mixpanel"}
          </p>
          {selectedUser ? (
            <p
              onClick={() => {
                selectedUser
                  ? navigator.clipboard.writeText(selectedUser.id)
                  : null
              }}
              style={{
                fontSize: 16,
                marginLeft: 20,
                opacity: selectedUser ? 0.1 : 0,
                cursor: selectedUser ? "pointer" : "default",
              }}
            >
              {selectedUser?.id ? "id" : "select a user"}
            </p>
          ) : null}
          {selectedUser ? (
            <p
              onClick={onClickDelete}
              style={{
                fontSize: 16,
                marginLeft: 20,
                opacity: 0.1,
                cursor: selectedUser ? "pointer" : "default",
              }}
            >
              {confirmDelete ? "confirm?" : "delete"}
            </p>
          ) : null}
        </div>
        <p
          onClick={onClickInvitedBy}
          style={{
            fontSize: 12,
            opacity: selectedUser?.invitedBy ? 0.5 : 0,
            cursor: selectedUser
              ? selectedUser?.invitedBy
                ? "pointer"
                : "auto"
              : "default",
          }}
          className={css`
            cursor: pointer;
          `}
        >
          invited by {selectedUser?.invitedBy?.username}
        </p>
        <p
          style={{
            fontSize: 12,
            opacity: selectedUser ? 0.5 : 0,
            cursor: selectedUser ? "auto" : "default",
          }}
        >
          {humanizeFileSize(selectedUser?.totalBytesUploaded)} uploaded
        </p>
        {/* <p
          style={{
            fontSize: 12,
            opacity: selectedUser?.onboardUserType ? 0.5 : 0,
            cursor: selectedUser ? "auto" : "default",
          }}
        >
          {selectedUser?.onboardUserType?.toUpperCase()}
        </p> */}
        <p
          style={{
            fontSize: 12,
            opacity: selectedUser ? 0.5 : 0,
            cursor: selectedUser ? "auto" : "default",
          }}
        >
          {selectedUser?.numFilesCreated} files created
        </p>
        <p
          style={{
            fontSize: 12,
            opacity: selectedUser ? 0.5 : 0,
            cursor: selectedUser ? "auto" : "default",
          }}
        >
          {selectedUser?.numDaysActive} days active
        </p>
        <p
          style={{
            fontSize: 12,
            opacity: selectedUser?.subscriptionProvider ? 0.5 : 0,
            cursor: selectedUser ? "auto" : "default",
          }}
        >
          {selectedUser?.subscriptionProvider}
        </p>
        <p
          title={new Date(selectedUser?.createdAt ?? 0).toString()}
          style={{
            fontSize: 12,
            opacity: selectedUser?.createdAt ? 0.5 : 0,
            cursor: selectedUser ? "auto" : "default",
          }}
        >
          {new Date(selectedUser?.createdAt ?? 0).toDateString()}{" "}
          {selectedUser?.signupPlatform
            ? `(${selectedUser?.signupPlatform})`
            : ""}
        </p>
        <p
          onClick={selectedUser ? onClickBan : undefined}
          style={{
            fontSize: 12,
            opacity: confirmBan ? 0.5 : selectedUser ? 0.1 : 0,
            cursor: selectedUser ? "pointer" : "default",
            color: confirmBan ? "red" : "black",
          }}
        >
          {confirmBan
            ? "confirm ?"
            : selectedUser?.banned
            ? "unban user"
            : "ban user"}
        </p>
        {/* <p
          style={{
            fontSize: 12,
            opacity: selectedUser?.versionMobile ? 0.1 : 0,
          }}
        >
          {selectedUser?.numMobilePushTokens} mobile push token
          {selectedUser?.numMobilePushTokens === 1 ? "" : "s"}
        </p> */}
      </div>
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "white",
          padding: 20,
          display: "flex",
          flexDirection: "row",
        }}
      >
        <MetricsToolbar
          onChangeSortBy={setSortedBy}
          onToggleChartMode={onToggleChartMode}
          includeAnon={includeAnon}
        />
        {chartMode
          ? null
          : userTree.map((rootUserId, i) => (
              <Users
                key={rootUserId}
                invitedById={rootUserId}
                sortedBy={sortedBy}
                onClickUser={(user) => addRow(user, i)}
                includeAnon={includeAnon}
                refetchTrigger={refetchTrigger}
              />
            ))}
      </div>
    </div>
  )
}

const Users: React.FC<{
  invitedById?: string | null
  sortedBy?: SortingOptions
  onClickUser: (user: User) => void
  includeAnon?: boolean
  refetchTrigger?: number
}> = ({ invitedById, sortedBy, onClickUser, includeAnon, refetchTrigger }) => {
  const [offset, setOffset] = useState(0)
  useEffect(() => {
    setOffset(0)
    setUsers(null)
  }, [sortedBy])
  const variables = {
    invitedById,
    sortBy: sortedBy,
    includeAnon,
    limit: 500,
    offset,
  }
  const { data, loading, error, refetch } = useQuery(USERS_QUERY, {
    variables,
    fetchPolicy: "network-only",
  })

  const onLoadMore = () => {
    refetch({ ...variables, limit: 10000, offset: 0 }).then((res) => {
      if (res.data) {
        setOffset((prev) => prev + res.data.length)
        setUsers((prev) => prev?.concat(data._users) ?? null)
      }
    })
  }

  const onLoadAll = () => {
    refetch({ ...variables, limit: 10000, offset: 0 }).then((res) => {
      if (res.data) {
        setOffset(0)
        setUsers(res.data._users)
      }
    })
  }

  useUpdateEffect(() => {
    refetch()
  }, [refetchTrigger])

  const [users, setUsers] = useState<User[] | null>(null)
  useEffect(() => {
    if (!users || !users.length) {
      setUsers(data?._users)
      setOffset(data?._users.length)
    }
  }, [data])
  console.log("users", users)
  const sortedUsers = users
  // const sortedUsers = useMemo(() => {
  //   if (!users) return null
  //   const sorted = sort((a, b) => {
  //     if (sortedBy === "numInvites") {
  //       return b.numInvites - a.numInvites
  //     } else if (sortedBy === "paid") {
  //       const aPaid = a.type === "pro"
  //       const bPaid = b.type === "pro"
  //       return aPaid === bPaid ? 0 : bPaid ? -1 : 1
  //     } else if (sortedBy === "lastSeen") {
  //       const aLastSeen = new Date(a.lastActiveAt ?? 0).getTime()
  //       const bLastSeen = new Date(b.lastActiveAt ?? 0).getTime()
  //       return aLastSeen - bLastSeen
  //     } else if (sortedBy === "mobile") {
  //       const aHasMobile = a.versionMobile != null
  //       const bHasMobile = b.versionMobile != null
  //       return aHasMobile === bHasMobile ? 0 : aHasMobile ? -1 : 1
  //     } else if (sortedBy === "desktop") {
  //       const aHasWeb = a.versionWeb != null
  //       const bHasWeb = b.versionWeb != null
  //       return aHasWeb === bHasWeb ? 0 : aHasWeb ? -1 : 1
  //     } else {
  //       return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  //     }
  //   }, users)
  //   return sorted
  // }, [users])

  // const list =
  //   el === "users"
  //     ? sortedUsers
  //     : users
  //         .find((u) => u.username === el)
  //         ?.invited.map((u) => {
  //           return users.find((x) => x.username === u)
  //         })
  const list = sortedUsers
  if (!list || list.length < 1) return null

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        borderRightStyle: "solid",
        borderRightWidth: 1,
        borderRightColor: "lightgrey",
        paddingRight: 10,
        marginRight: 10,
      }}
    >
      {list.map((user, i) => {
        if (!user) return
        return (
          <UserCell key={user.username} user={user} onClick={onClickUser} />
        )
      })}
      <button onClick={() => onLoadMore()}>load more</button>
      <button onClick={() => onLoadAll()}>load all</button>
    </div>
  )
}

export default UserList
